import { ChevronDown, ChevronsDown, ChevronsUp, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { HierarchicalGroupNode } from '@/types';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface GroupDividerProps {
  group: HierarchicalGroupNode;
  isExpanded: boolean;
  onToggle: () => void;
  onZoomToGroup: () => void;
  onToggleAllDescendants?: () => void;
  areAllDescendantsExpanded?: boolean;
  hasChildren?: boolean; // Whether this node has child groups (not just units)
  indentLevel: number;
  isMobile?: boolean; // Enable mobile-specific styling
  isHighlighted?: boolean; // Highlight when hovered unit is in this group
  disableCollapse?: boolean; // Prevent collapsing (for single top-level company)
}

export function GroupDivider({
  group,
  isExpanded,
  onToggle,
  onZoomToGroup,
  onToggleAllDescendants,
  areAllDescendantsExpanded = false,
  hasChildren = false,
  indentLevel,
  isMobile = false,
  isHighlighted = false,
  disableCollapse = false,
}: GroupDividerProps) {
  const indentPx = indentLevel * (isMobile ? 12 : 16); // Reduced indentation for mobile
  const isUnassigned = group.name === 'Unassigned';

  return (
    <div className="group/divider">
      <Separator className="mt-0.5" />
      <div
        onClick={onToggle}
        className={cn(
          "flex items-center justify-between px-2 py-0.5 transition-all cursor-pointer sticky top-0 z-10 relative",
          // Hierarchical background colors based on indent level
          !isHighlighted && [
            // Level 0 (Company) - Darkest (fully opaque for maximum contrast)
            indentLevel === 0 && (
              isUnassigned
                ? isMobile ? "bg-muted/60 active:bg-muted/80" : "bg-muted/60 hover:bg-muted/80"
                : isMobile ? "bg-muted/95 active:bg-muted" : "bg-muted/95 hover:bg-muted"
            ),
            // Level 1 (Production) - Medium
            indentLevel === 1 && (
              isUnassigned
                ? isMobile ? "bg-muted/35 active:bg-muted/55" : "bg-muted/35 hover:bg-muted/55"
                : isMobile ? "bg-muted/55 active:bg-muted/75" : "bg-muted/55 hover:bg-muted/75"
            ),
            // Level 2+ (Site) - Lightest (minimal opacity for maximum contrast)
            indentLevel >= 2 && (
              isUnassigned
                ? isMobile ? "bg-muted/10 active:bg-muted/30" : "bg-muted/10 hover:bg-muted/30"
                : isMobile ? "bg-muted/20 active:bg-muted/40" : "bg-muted/20 hover:bg-muted/40"
            ),
          ],
          // Highlight styles when ancestor of hovered unit - clean muted HT green
          isHighlighted && "!bg-[#8FB560]/20 dark:!bg-[#8FB560]/15"
        )}
        style={{
          paddingLeft: `${indentPx + 8}px`
        }}
      >
        {/* Left side: Chevron + Group name + Unit count */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {!disableCollapse ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className={cn(
                "rounded transition-colors flex-shrink-0",
                isMobile ? "p-2 active:bg-muted" : "p-0.5 hover:bg-muted"
              )}
              aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
              aria-expanded={isExpanded}
            >
              <motion.div
                animate={{ rotate: isExpanded ? 0 : -90 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className={cn(
                  "text-muted-foreground",
                  isMobile ? "h-4 w-4" : "h-3.5 w-3.5"
                )} />
              </motion.div>
            </button>
          ) : (
            // Empty spacer when chevron is hidden to maintain alignment
            <div className={cn(
              "flex-shrink-0",
              isMobile ? "w-8" : "w-4"
            )} />
          )}

          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className={cn(
              "text-[10px] uppercase tracking-wider truncate transition-colors",
              // Font weight hierarchy
              isHighlighted ? "font-semibold" : indentLevel === 0 ? "font-bold" : "font-medium",
              // Font style hierarchy
              indentLevel >= 2 && !isUnassigned && "italic",
              // Color - dark text on light green background when highlighted
              isHighlighted ? "text-foreground dark:text-foreground" : "text-muted-foreground",
              isUnassigned && !isHighlighted && "italic opacity-70"
            )}>
              {group.name}
            </span>
            <span className={cn(
              "text-xs flex-shrink-0 transition-colors",
              isHighlighted ? "text-foreground/90 dark:text-foreground/90" : "text-muted-foreground/70",
              indentLevel === 0 && "font-semibold"
            )}>
              ({group.unitCount})
            </span>
          </div>
        </div>

        {/* Right side: Expand-all + Zoom icons */}
        <div className="flex items-center flex-shrink-0">
          {/* Expand/collapse all descendants — only for nodes with children */}
          {hasChildren && onToggleAllDescendants && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleAllDescendants();
              }}
              className={cn(
                "rounded transition-colors flex-shrink-0",
                isMobile
                  ? "p-2 active:bg-accent/50 opacity-100"
                  : "p-1 hover:bg-accent/50 opacity-30 group-hover/divider:opacity-100"
              )}
              aria-label={areAllDescendantsExpanded ? `Collapse all under ${group.name}` : `Expand all under ${group.name}`}
              title={areAllDescendantsExpanded ? `Collapse all under ${group.name}` : `Expand all under ${group.name}`}
            >
              {areAllDescendantsExpanded ? (
                <ChevronsUp className={cn(
                  "text-muted-foreground",
                  isMobile ? "h-4 w-4" : "h-3.5 w-3.5"
                )} />
              ) : (
                <ChevronsDown className={cn(
                  "text-muted-foreground",
                  isMobile ? "h-4 w-4" : "h-3.5 w-3.5"
                )} />
              )}
            </button>
          )}

          {/* Zoom to group */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onZoomToGroup();
            }}
            className={cn(
              "rounded transition-colors flex-shrink-0",
              isMobile
                ? "p-2 active:bg-accent/50 opacity-100"
                : "p-1 hover:bg-accent/50 opacity-30 group-hover/divider:opacity-100"
            )}
            aria-label={`Zoom to ${group.name}`}
            title={`Zoom map to ${group.name}`}
          >
            <MapPin className={cn(
              "text-muted-foreground",
              isMobile ? "h-4 w-4" : "h-3.5 w-3.5"
            )} />
          </button>
        </div>
      </div>
    </div>
  );
}
